<script lang="ts" setup>
import { usePopper } from '@shared/layout/usePopper';
import { formatDate, isInvalidDate, makeDate, toDateonly } from '@tools/dates';
import { onClickOutside } from '@vueuse/core';
import DatePicker, { type DatePickerEmitsOptions, type DatePickerProps } from 'primevue/datepicker';
import { computed, ref, watch } from 'vue';
import { useApplicationRoot } from '@tools/applicationRoot';
import { deepmerge } from 'deepmerge-ts';
import VInputMask, { type VInputMaskProps } from '../VInputMask/VInputMask.vue';

export interface VDatePickerProps {
  invalid?: DatePickerProps['invalid'];
  readonly?: DatePickerProps['readonly'];
  disabled?: DatePickerProps['disabled'];
  minDate?: DatePickerProps['minDate'];
  maxDate?: DatePickerProps['maxDate'];
  showButtonBar?: DatePickerProps['showButtonBar'];
  clearable?: boolean;
  fullWidth?: boolean;
  inputmaskPt?: VInputMaskProps['pt'];
  datepickerPt?: DatePickerProps['pt'];
}

const props = withDefaults(defineProps<VDatePickerProps>(), {
  showButtonBar: true,
  clearable: true,
});

const emit = defineEmits<{
  blur: [];
}>();

defineOptions({ inheritAttrs: false });

const today = new Date();
const showTodayButton = computed(() => {
  if (props.minDate !== undefined && props.maxDate !== undefined)
    return today >= props.minDate && today <= props.maxDate;
  if (props.minDate !== undefined) return today >= props.minDate;
  if (props.maxDate !== undefined) return today <= props.maxDate;
  return true;
});

const modelValue = defineModel<Date | undefined>('modelValue');
const inputMaskValue = ref<string | undefined>(
  !modelValue.value ? undefined : formatDate(makeDate(modelValue.value)),
);
const datepickerValue = ref<Date | null>(modelValue.value || null);
const applicationRoot = useApplicationRoot();

const volledigeDatumLengte = 'dd-mm-jjjj'.length;
const onbruikbareInvoer = ref(false);
const isOngeldig = computed(() => Boolean(props.invalid) || onbruikbareInvoer.value);

const rootElement = ref();
const datepickerElement = ref();
const {
  isOpen: datepickerIsOpen,
  open: openDatepicker,
  close: closeDatepicker,
  toggle: toggleDatepicker,
  popperStyles,
} = usePopper(datepickerElement, rootElement);

onClickOutside(datepickerElement, () => {
  if (!rootElement.value?.contains(document.activeElement)) {
    closeDatepicker();
  }
});

function onBlur() {
  const invoer = inputMaskValue.value ?? '';
  if (invoer.length > 0 && invoer.length < volledigeDatumLengte) {
    const huidige = modelValue.value;
    if (huidige !== undefined && !isInvalidDate(huidige)) {
      inputMaskValue.value = formatDate(makeDate(huidige));
      onbruikbareInvoer.value = false;
    } else {
      onbruikbareInvoer.value = true;
    }
  }

  emit('blur');
  setTimeout(() => {
    if (
      !rootElement.value?.contains(document.activeElement) &&
      !datepickerElement.value?.$el.contains(document.activeElement) &&
      document.activeElement !== document.body
    ) {
      closeDatepicker();
    }
  }, 10);
}

function tryParseDate(input: string): Date | undefined {
  // In JS is het mogelijk om new Date('2025-02-31') te doen, dan komt daar 3 maart 2025 uit rollen.
  // Dit soort auto-magisch corrigeren lijkt me ongewenst, daarom deze functie geschreven om te bepalen wanneer de invoer wel of niet geldig is.
  const isoFormat = `${input.slice(6, 10)}-${input.slice(3, 5)}-${input.slice(0, 2)}`;
  const parsedDate = new Date(isoFormat);
  if (isInvalidDate(parsedDate)) return undefined;

  // Controle op het jaar duizend omdat er anders al bij 01-01-198 een waarde wordt teruggegeven.
  if (parsedDate.getFullYear() < 1000) return undefined;
  if (formatDate(makeDate(parsedDate)) !== input) return undefined;

  return parsedDate;
}

function binnenBereik(datum: Date) {
  const waarde = toDateonly(datum);
  if (props.minDate !== undefined && waarde < toDateonly(props.minDate)) return false;
  if (props.maxDate !== undefined && waarde > toDateonly(props.maxDate)) return false;
  return true;
}

let schrijftEigenWaarde = false;

function schrijfDatum(datum: Date | undefined) {
  const huidige = modelValue.value;
  if (huidige === datum) return;
  if (huidige !== undefined && datum !== undefined && huidige.getTime() === datum.getTime()) return;

  schrijftEigenWaarde = true;
  modelValue.value = datum;
}

function onMaskValueUpdated(newValue: string | null | undefined) {
  const invoer = newValue ?? '';

  if (invoer.length === 0) {
    onbruikbareInvoer.value = false;
    datepickerValue.value = null;
    schrijfDatum(undefined);
    return;
  }

  if (invoer.length < volledigeDatumLengte) {
    onbruikbareInvoer.value = false;
    return;
  }

  const datum = tryParseDate(invoer);
  if (datum === undefined) {
    onbruikbareInvoer.value = true;
    schrijfDatum(undefined);
    return;
  }

  onbruikbareInvoer.value = !binnenBereik(datum);
  datepickerValue.value = datum;
  schrijfDatum(datum);
  closeDatepicker();
}

watch([() => props.minDate, () => props.maxDate], () => {
  const invoer = inputMaskValue.value ?? '';
  if (invoer.length < volledigeDatumLengte) return;

  const datum = tryParseDate(invoer);
  if (datum === undefined) return;

  onbruikbareInvoer.value = !binnenBereik(datum);
});

const onDateSelected: DatePickerEmitsOptions['update:modelValue'] = (date) => {
  if (Array.isArray(date)) throw new Error('VDatePicker ondersteund nog geen date ranges');

  closeDatepicker();

  onbruikbareInvoer.value = false;

  if (date === null || date === undefined) {
    modelValue.value = undefined;
    inputMaskValue.value = undefined;
    return;
  }

  modelValue.value = date;
  inputMaskValue.value = formatDate(makeDate(date));
};

watch(modelValue, (newValue) => {
  if (schrijftEigenWaarde) {
    schrijftEigenWaarde = false;
    return;
  }

  onbruikbareInvoer.value = false;

  if (newValue === null || newValue === undefined || isInvalidDate(newValue)) {
    inputMaskValue.value = undefined;
    datepickerValue.value = null;
    return;
  }

  inputMaskValue.value = formatDate(makeDate(newValue));
  datepickerValue.value = newValue;
});

const nonce = Math.ceil(Math.random() * 1_000_000).toString();

const extendDatepickerPt = computed<DatePickerProps['pt']>(() =>
  deepmerge(
    {
      root: { id: `calendar-${nonce}` },
    },
    props?.datepickerPt,
  ),
);

const extendInputmaskPt = computed<VInputMaskProps['pt']>(() =>
  deepmerge(
    {
      root: {
        'aria-controls': `calendar-${nonce}`,
        'aria-expanded': datepickerIsOpen.value,
        'aria-haspopup': 'grid',
      },
    },
    props?.inputmaskPt,
  ),
);
</script>

<template>
  <div
    class="relative"
    :class="[{ filled: modelValue }, { 'w-full': props.fullWidth }]"
    ref="rootElement"
  >
    <VInputMask
      v-bind="$attrs"
      v-model="inputMaskValue"
      class="max-w-full"
      :class="{ 'border-red-500! bg-red-100/50!': isOngeldig }"
      data-testid="date-picker__input-mask"
      :disabled="props.disabled"
      :full-width="props.fullWidth"
      :icon="['far', 'calendar-range']"
      icon-clickable
      :invalid="isOngeldig"
      mask="##-##-####"
      placeholder="dd-mm-jjjj"
      :pt="extendInputmaskPt"
      :readonly="props.readonly"
      @blur="onBlur"
      @focus="openDatepicker"
      @icon-clicked="toggleDatepicker"
      @update:model-value="onMaskValueUpdated"
    />
    <div
      class="absolute z-widget transition-all duration-150"
      :class="{
        'opacity-0': !datepickerIsOpen,
        'opacity-100': datepickerIsOpen,
        'translate-y-[-5%]': !datepickerIsOpen,
        'translate-y-0': datepickerIsOpen,
        'pointer-events-none': !datepickerIsOpen,
      }"
    >
      <Teleport :to="applicationRoot" defer>
        <!-- @vue-expect-error geeft errror op show-today-button maar die bestaat nog, in nieuwe versie van primevue werkt dit anders -->
        <DatePicker
          ref="datepickerElement"
          inline
          v-model="datepickerValue"
          v-if="datepickerIsOpen"
          :invalid="isOngeldig"
          :readonly="props.readonly"
          :disabled="props.disabled"
          :min-date="props.minDate"
          :max-date="props.maxDate"
          :show-button-bar="props.showButtonBar"
          :show-today-button="showTodayButton"
          :pt="extendDatepickerPt"
          data-testid="v-calendar"
          date-format="dd-mm-yy"
          @update:model-value="onDateSelected"
          :style="popperStyles"
        />
      </Teleport>
    </div>
  </div>
</template>
